import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { CopyOutlined, MessageOutlined } from "@ant-design/icons";
import { Badge, Button, Layout, Space, Typography } from "antd";
import { formatDistanceToNow } from "date-fns";
import { getCountryCallingCode } from "libphonenumber-js";
import { getFilterField } from "@/Assets/Mocks/User.mock";
import CommonTable from "@/Components/Common/CommonTable/CommonTable";
import { ProfileContext } from "@/Context/ProfileContext";

const { Text, Link } = Typography;

function DeletedUser() {
  const profileData = useContext(ProfileContext);
  const childRef = useRef();
  const [copied, setCopied] = useState(false);

  const copyToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
    setCopied(true);
  };
  const getTrialDays = () => {
    const trial = {
      days: profileData?.trial_days,
      start: !profileData?.trial_start ? new Date() : profileData?.trial_start,
    };
    const dt2 = new Date();
    const dt1 = new Date(trial.start);
    const remainingDays =
      trial.days -
      Math.floor(
        (Date.UTC(dt2.getFullYear(), dt2.getMonth(), dt2.getDate()) -
          Date.UTC(dt1.getFullYear(), dt1.getMonth(), dt1.getDate())) /
          (1000 * 60 * 60 * 24)
      );
    return remainingDays > 0 ? remainingDays : 0;
  };

  const formatTimeAgo = (dateString) => {
    if (dateString) {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } else {
      return "N/A";
    }
  };

  const handleIconClick = (phoneNumber, countryCode) => {
    const callingCode = getCountryCallingCode(countryCode);
    const whatsappLink = `https://wa.me/${callingCode}${phoneNumber}`;
    window.open(whatsappLink, "_blank");
  };

  const filterFormFields = useMemo(() => getFilterField(), []);

  const ArchivedData = useCallback((rows) => {
    if (rows?.length <= 0) return [];
    return rows?.map((row, index) => {
      const featureStatus = row?.featureStatus;
      const feedbackArray = featureStatus ? Object.keys(featureStatus) : [];
      const filteredItems = feedbackArray.filter((item) => item !== "brokenLinksEnableDate");
      return {
        key: row._id,
        no: index + 1,
        name: (
          <Space direction="vertical" size="small">
            <Link href={`https://${row.shopUrl}`} target="_blank">
              {row.storeName}
            </Link>
            <Space size="middle">
              <Text>{row?.shopUrl}</Text>
              <Button
                type="text"
                icon={<CopyOutlined />}
                size="small"
                onClick={() => copyToClipboard(row.shopUrl)}
              />
            </Space>
            <Text>{row?.shop_owner}</Text>
            <Space direction="vertical" size="small">
              {Array.isArray(row?.feedback) &&
                row?.feedback?.map((item, index) => (
                  <Text strong key={index}>
                    {" "}
                    {item.id}
                  </Text>
                ))}
            </Space>
          </Space>
        ),
        email: (
          <Space direction="vertical" size="small">
            <Text>{row?.country_name}</Text>
            <Text>Currency:{row.currency}</Text>
            <Space size="middle">
              <Text>{row?.email}</Text>
              <Button
                type="text"
                icon={<CopyOutlined />}
                size="small"
                onClick={() => copyToClipboard(row.email)}
              />
            </Space>
            {row.phone && (
              <Space size="middle">
                <Text>{row?.phone}</Text>
                <Button
                  type="text"
                  icon={<MessageOutlined />}
                  size="small"
                  onClick={() => handleIconClick(row.phone, row.country_code)}
                />
              </Space>
            )}
            <Text>Product Count : {row?.productCount}</Text>
            <Text>Installed: {formatTimeAgo(row?.created)}</Text>
            {row.created_at ? <Text>Created : {formatTimeAgo(row?.created_at)}</Text> : null}
          </Space>
        ),
        plan: (
          <Space direction="vertical" size="small">
            <Text>App Plan : {row?.recurringPlanName}</Text>
            <Text>Free Trial: {getTrialDays()} days</Text>
            <Text>Shopify Plan : {row?.plan_display_name}</Text>
            <Text>
              Onboard:
              {row.isOnBoardingDone ? <Badge color="green">Y</Badge> : <Badge color="orange">N</Badge>}
            </Text>
          </Space>
        ),
        deleted: <Text>{row?.deleted ? formatTimeAgo(row.deleted) : ""}</Text>,
      };
    });
  }, []);

  const columns = [
    {
      title: "No",
      dataIndex: "no",
      key: "no",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Plan",
      dataIndex: "plan",
      key: "plan",
    },
    {
      title: "Deleted",
      dataIndex: "deleted",
      key: "deleted",
    },
  ];

  return (
    <Layout.Content style={{ padding: "0", width: "100%" }}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <CommonTable
          resourceName={{
            singular: "User",
            plural: "Users",
          }}
          title="Deleted User"
          queryPlaceholder="Search User by (shopUrl, email, storeName, recurringPlanId, recurringPlanName)"
          url={`admin/deleteduser`}
          selectable={false}
          rowsData={ArchivedData}
          isFilterVisible
          columns={columns}
          searchKey={["storeName", "shopUrl", "email", "recurringPlanId", "recurringPlanName"]}
          ref={childRef}
          isAdd={false}
          verticalAlign="center"
          columnContentTypes={["text", "text", "numeric", "numeric", "numeric", "text", "text"]}
          filterFormFields={filterFormFields}
        />
      </Space>
    </Layout.Content>
  );
}

export default DeletedUser;
